import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardBody,
  HStack,
  VStack,
  Text,
  Heading,
  Button,
  ButtonGroup,
  Spinner,
  Center,
  Badge,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import proxy from "../proxy";

const RANGES = [
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
];

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtPct = (n) =>
  `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(2)}%`;

const formatTick = (dateStr) => {
  // Compact axis label: "Jun 13"
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatTooltipDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  const change = Number(point.daily_change || 0);
  const changeColor = change > 0 ? "green.300" : change < 0 ? "red.300" : "gray.300";

  return (
    <Box
      bg="gray.800"
      p={3}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="teal.500"
      boxShadow="0 0 20px rgba(56, 178, 172, 0.3)"
    >
      <Text fontSize="xs" color="gray.400" mb={1}>
        {formatTooltipDate(point.date)}
      </Text>
      <Text fontSize="md" fontWeight="bold" color="white">
        {fmtMoney(point.portfolio_value)}
      </Text>
      {change !== 0 && (
        <Text fontSize="xs" color={changeColor} mt={1}>
          {change > 0 ? "▲" : "▼"} {fmtMoney(Math.abs(change))} vs. day before
        </Text>
      )}
    </Box>
  );
};

const PortfolioGrowthChart = () => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  const chartHeight = useBreakpointValue({ base: 160, md: 200 });

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    axios
      .get(`${proxy}server_mts/GetPortfolioHistory.php?days=${days}`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (cancelled) return;
        setHistory(Array.isArray(res.data) ? res.data : []);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load portfolio history.");
      })
      .finally(() => !cancelled && setIsLoading(false));

    return () => { cancelled = true; };
  }, [days]);

  const { latest, first, deltaAbs, deltaPct, isUp } = useMemo(() => {
    if (history.length === 0) {
      return { latest: 0, first: 0, deltaAbs: 0, deltaPct: 0, isUp: true };
    }
    const latest = history[history.length - 1].portfolio_value;
    const first  = history[0].portfolio_value;
    const deltaAbs = latest - first;
    const deltaPct = first > 0 ? (deltaAbs / first) * 100 : 0;
    return { latest, first, deltaAbs, deltaPct, isUp: deltaAbs >= 0 };
  }, [history]);

  const accent = isUp ? "#48BB78" : "#F56565";
  const accentSoft = isUp ? "rgba(72, 187, 120, 0.2)" : "rgba(245, 101, 101, 0.2)";

  return (
    <Card
      bg="rgba(45, 55, 72, 0.7)"
      borderWidth="1px"
      borderColor="teal.700"
      backdropFilter="blur(10px)"
      boxShadow="0 0 30px rgba(56, 178, 172, 0.15)"
      position="relative"
      maxW="900px"
      mx="auto"
    >
      <CardBody p={{ base: 3, md: 4 }}>
        <VStack spacing={3} align="stretch">

          {/* Header row */}
          <HStack
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
            spacing={3}
          >
            <VStack spacing={0} align="flex-start">
              <Heading size="sm" color="teal.300">
                📈 Portfolio Growth
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Daily snapshots from AI insights
              </Text>
            </VStack>

            <ButtonGroup size="xs" isAttached variant="outline">
              {RANGES.map((r) => (
                <Button
                  key={r.days}
                  onClick={() => setDays(r.days)}
                  bg={days === r.days ? "teal.500" : "transparent"}
                  color={days === r.days ? "white" : "teal.200"}
                  borderColor="teal.600"
                  _hover={{ bg: days === r.days ? "teal.500" : "whiteAlpha.100" }}
                  fontWeight="bold"
                >
                  {r.label}
                </Button>
              ))}
            </ButtonGroup>
          </HStack>

          {/* Current value + delta */}
          {!isLoading && !error && history.length > 0 && (
            <HStack justify="space-between" align="flex-end" flexWrap="wrap" gap={2}>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                  Current value
                </Text>
                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="white">
                  {fmtMoney(latest)}
                </Text>
              </VStack>

              <VStack spacing={0} align="flex-end">
                <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                  Since {history[0].date}
                </Text>
                <HStack spacing={2}>
                  <Badge
                    colorScheme={isUp ? "green" : "red"}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                  >
                    {isUp ? "▲" : "▼"} {fmtPct(deltaPct)}
                  </Badge>
                  <Text fontSize="sm" color={isUp ? "green.300" : "red.300"} fontWeight="semibold">
                    {deltaAbs >= 0 ? "+" : "−"}{fmtMoney(Math.abs(deltaAbs))}
                  </Text>
                </HStack>
              </VStack>
            </HStack>
          )}

          {/* Chart body */}
          <Box minH={chartHeight}>
            {isLoading ? (
              <Center h={chartHeight}>
                <Spinner size="md" color="teal.300" thickness="3px" />
              </Center>
            ) : error ? (
              <Center h={chartHeight}>
                <VStack spacing={1}>
                  <Text fontSize="2xl">📉</Text>
                  <Text fontSize="sm" color="red.300">{error}</Text>
                </VStack>
              </Center>
            ) : history.length < 2 ? (
              <Center h={chartHeight}>
                <VStack spacing={1}>
                  <Text fontSize="2xl">📊</Text>
                  <Text fontSize="sm" color="gray.400">
                    Not enough data yet — needs at least 2 daily snapshots.
                  </Text>
                </VStack>
              </Center>
            ) : (
              <ResponsiveContainer width="100%" height={chartHeight}>
                <AreaChart
                  data={history}
                  margin={{ top: 5, right: 5, left: -10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={accent} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.06)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#A0AEC0" }}
                    stroke="rgba(255,255,255,0.15)"
                    tickLine={false}
                    tickFormatter={formatTick}
                    minTickGap={32}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#A0AEC0" }}
                    stroke="rgba(255,255,255,0.15)"
                    tickLine={false}
                    width={50}
                    domain={["dataMin - 5", "dataMax + 5"]}
                    tickFormatter={(v) => `$${Math.round(v)}`}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: accentSoft, strokeWidth: 1 }} />
                  <Area
                    type="monotone"
                    dataKey="portfolio_value"
                    stroke={accent}
                    strokeWidth={2}
                    fill="url(#growthGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: accent, stroke: "#1A202C", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Footer: snapshot count */}
          {!isLoading && history.length > 0 && (
            <HStack justify="center" pt={1}>
              <Badge colorScheme="teal" fontSize="2xs" px={2} py={0.5}>
                📊 {history.length} snapshots
              </Badge>
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default PortfolioGrowthChart;
